import React, { useEffect, useState } from 'react';
import {
    Backdrop,
    Box,
    Button,
    Fade,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateField } from '@mui/x-date-pickers/DateField';
import dayjs from 'dayjs';

import NewCustomer from '../../api/customers/new_customer.ts';
import { CustomerErrorAlert, CustomerSuccessAlert } from '../alert/customerAlert.js';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomersAsync } from '../../slices/customerSlice.js';
import { UpdateCustomerApi } from '../../api/customers/update_customer.ts';

const StyledBackdrop = (props) => (
    <Backdrop {...props} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }} />
);

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '80%', md: '70%', lg: '60%' },
    maxHeight: { xs: '85dvh', md: '90vh' },
    overflowY: 'auto',
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: { xs: 2, md: 4 },
    outline: 'none',
};

interface FormCustomerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
}

const FormCustomer: React.FC<FormCustomerProps> = ({ open, setOpen, action, customer }) => {
    const handleClose = () => {
        setOpen(false);
    };

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectGender, setSelectGender] = useState(['Hombre', 'Mujer']);
    const offices = useSelector((state) => state.offices.data);
    const cities = useSelector((state) => state.cities.data);
    const dataType = useSelector((state) => state.dataTypes.data);
    const customerType = useSelector((state) => state.customerType.data);
    const customerGroup = useSelector((state) => state.customerGroup.data);

    const [formData, setFormData] = useState({
        name: '',
        document: '',
        date_birth: '',
        email: '',
        phone: '',
        phone_alternating: '',
        address: '',
        gender: '',
        status_id: 1,
        customer_group_id: '',
        customer_type_id: '',
    });

    const dispatch = useDispatch();

    useEffect(() => {
        if (!open) return;

        if (action === 'updateCustomer' && customer) {
            setFormData({
                name: customer.name || '',
                document: customer.document || '',
                date_birth: customer.date_birth ? dayjs(customer.date_birth) : null,
                email: customer.email || '',
                phone: customer.phone || '',
                phone_alternating: customer.phone_alternating || '',
                office_id: customer.office_id || '',
                city_id: customer.city_id || '',
                address: customer.address || '',
                gender: customer.gender || '',
                customer_group_id: customer.id_customer_group || '',
                customer_type_id: customer.id_customer_type || '',
                status_id: customer.status_id || 1,
            });
        } else if (action === 'createCustomer') {
            setFormData({
                name: '',
                document: '',
                date_birth: null,
                email: '',
                phone: '',
                phone_alternating: '',
                office_id: '',
                city_id: '',
                address: '',
                gender: '',
                status_id: 1,
                customer_group_id: '',
                customer_type_id: '',
            });
        }
    }, [open, action, customer]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (submitting) return;

        setSubmitting(true);

        const payload = {
            name: formData.name.trim(),
            document: formData.document.trim(),
            date_birth: formData.date_birth?.format('YYYY-MM-DD') || null,
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            phone_alternating: formData.phone_alternating.trim(),
            address: formData.address.trim(),
            gender: formData.gender.trim(),
            status_id: formData.status_id,
            customer_group_id: formData.customer_group_id ? Number(formData.customer_group_id) : null,
            customer_type_id: formData.customer_type_id ? Number(formData.customer_type_id) : null,
        };

        try {
            await NewCustomer(payload);
            dispatch(fetchCustomersAsync());
            handleClose();
            CustomerSuccessAlert('Cliente creado exitosamente', '#1976d2');
        } catch (err) {
            handleClose();
            CustomerErrorAlert('Error al crear el cliente, intente nuevamente.', '#d32f2f');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async () => {
        if (submitting) return;

        setSubmitting(true);

        const payload = {
            id_customer: customer.id_customer,
            name: formData.name.trim(),
            document: formData.document.trim(),
            date_birth: formData.date_birth,
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            phone_alternating: formData.phone_alternating.trim(),
            address: formData.address.trim(),
            gender: formData.gender.trim(),
            status_id: formData.status_id,
        };

        try {
            await UpdateCustomerApi(payload);
            dispatch(fetchCustomersAsync());
            handleClose();
            CustomerSuccessAlert('Cliente actualizado exitosamente', '#1976d2');
        } catch (err) {
            handleClose();
            CustomerErrorAlert('Error al actualizar el cliente, intente nuevamente.', '#d32f2f');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: StyledBackdrop }}
            aria-labelledby="modal-title"
        >
            <Fade in={open}>
                <Box sx={modalStyle}>

                    {(loading || submitting) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                bgcolor: 'rgba(255,255,255,0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                borderRadius: 3,
                            }}
                        >
                            <CircularProgress size={60} thickness={5} />
                        </Box>
                    )}

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container alignItems="center" justifyContent="space-between" mb={4}>
                            <Typography id="modal-title" variant="h5" component="h2" fontWeight="bold" color="primary">
                                {action === 'createCustomer' ? 'Crear cliente' : 'Actualizar cliente'}
                            </Typography>
                            <Button onClick={handleClose} color="error" size="large" sx={{ minWidth: 40 }}>
                                X
                            </Button>
                        </Grid>

                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Nombres"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Número de documento"
                                    name="document"
                                    value={formData.document}
                                    onChange={handleChange}
                                    placeholder="1012345678"
                                    inputProps={{ maxLength: 15 }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth disabled={loading}>
                                    <InputLabel>Tipo de cliente</InputLabel>
                                    <Select
                                        name="customer_type_id"
                                        value={formData.customer_type_id}
                                        label="Tipo de cliente"
                                        onChange={handleChange}
                                    >
                                        {customerType.map((type) => (
                                            <MenuItem key={type.id_customer_type} value={type.id_customer_type} >
                                                {type.type_customer}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth disabled={loading}>
                                    <InputLabel>Grupo de cliente</InputLabel>
                                    <Select
                                        name="customer_group_id"
                                        value={formData.customer_group_id}
                                        label="Grupo de cliente"
                                        onChange={handleChange}
                                    >
                                        {customerGroup.map((group) => (
                                            <MenuItem key={ group.id_customer_group} value={group.id_customer_group}>
                                                {group.group}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DateField
                                    label="Fecha de nacimiento "
                                    value={formData.date_birth ? dayjs(formData.date_birth) : null}
                                    onChange={(newValue) => setFormData(prev => ({ ...prev, date_birth: newValue }))}
                                    format="DD/MM/YYYY"
                                    maxDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                            helperText: 'Formato: Día/Mes/Año',
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth disabled={loading}>
                                    <InputLabel>Genero</InputLabel>
                                    <Select
                                        name="gender"
                                        value={formData.gender}
                                        label="Genero"
                                        onChange={handleChange}
                                    >
                                        {selectGender.map((gender) => (
                                            <MenuItem key={gender} value={gender}>
                                                {gender}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Teléfono"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+57 300 123 4567"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">+57</InputAdornment>,
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Teléfono alternativo"
                                    name="phone_alternating"
                                    value={formData.phone_alternating || ''}
                                    onChange={handleChange}
                                    placeholder="+57 300 123 4567"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">+57</InputAdornment>,
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Correo electrónico"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </Grid>

                            {/* <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth disabled={loading}>
                                    <InputLabel>Ciudad</InputLabel>
                                    <Select
                                        name="city_id"
                                        value={formData.city_id}
                                        label="Ciudad"
                                        onChange={handleSelectChange}
                                    >
                                        {cities.map((city) => (
                                            <MenuItem key={city.id_city} value={city.id_city}>
                                                {city.city_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth disabled={loading}>
                                    <InputLabel>Oficina</InputLabel>
                                    <Select
                                        name="office_id"
                                        value={formData.office_id}
                                        label="Oficina"
                                        onChange={handleSelectChange}
                                    >
                                        {offices.map((office) => (
                                            <MenuItem key={office.id_office} value={office.id_office}>
                                                {office.office_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid> */}

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth disabled={loading}>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        name="status_id"
                                        value={formData.status_id}
                                        label="Estado"
                                        onChange={handleSelectChange}
                                    >
                                        {dataType.map((status) => (
                                            <MenuItem key={status.id_type_data} value={status.id_type_data}>
                                                {status.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Dirección completa"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                        </Grid>
                        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={5}>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={handleClose}
                                disabled={loading || submitting}
                            >
                                Cancelar
                            </Button>
                            {
                                action == 'updateCustomer' && (
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleUpdate}
                                        disabled={loading || submitting}
                                    >
                                        {submitting ? 'Actualizando...' : 'Guardar Cambios'}
                                    </Button>
                                ) || action == 'createCustomer' && (
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleSubmit}
                                        disabled={loading || submitting}
                                    >
                                        {submitting ? 'Guardando...' : 'Guardar Cliente'}
                                    </Button>
                                )
                            }
                        </Stack>
                    </LocalizationProvider>
                </Box>
            </Fade>
        </Modal>
    );
};

export default FormCustomer;